import {
    DataLayerEventName,
    PagePosition,
    PlayerEvent,
    PlaylistType,
    Standard,
    VideoConfig,
    VideoDetails,
    VideoQueueItemMeta,
} from '@news-mono/web-common'
import { VideoMetaDTO } from '@west-australian-newspapers/publication-types'
import React from 'react'
import { Logger } from '@west-australian-newspapers/typescript-log'
import countdownTimer from '../../../../content/Video/components/TitleOverlay/countdown-timer'
import {
    brightcovePlayerWebId,
    RuntimeVideoConfig,
} from '../../../../content/Video/providers/PlayerProps'
import {
    AutoplayAllowState,
    videoCanAutoplay,
} from '../../../../content/Video/utils/can-autoplay'
import {
    getMetadata,
    parseSourceFromMetadata,
} from '../../../../content/Video/utils/get-metadata'
import { VideoQueue } from '../../../../templates/Publication/lib/get-video-queue'
import { ViewportProps } from '../../../../__helpers/in-viewport'
import { ThemeProps } from '../../../../__styling/themes'
import { swmPlayerDebug } from '../../../../content/Video/providers/debug-log'
import {
    AutoplayCountdown,
    Other,
    RenderPlayerOptions,
} from '../../../../content/Video/player-render-params'
import {
    AutoplayOptions,
    GetVideoQueue,
    PlayerProvider,
    VideoOptions,
} from '../../../../content/Video/PlayerInterface'

export interface PNRenderPlayerParams extends ThemeProps {
    playerActions: {
        handleMuteToggle: () => void
        playVideo: () => void
        stopAutoplay: () => void
        pauseVideo: () => void
        resumeVideo: () => void
        handleUserInactive: () => void
    }
    playerOptions: RenderPlayerOptions
    playerState: {
        inViewport: number
        isMuted: boolean
        adStarted: boolean
        adPlayed: boolean
        showTopBar: boolean
        showInternalPlayButton: boolean
        isFullscreen: boolean
        adPlaying: boolean
        adPaused: boolean
        videoStarted: boolean
        videoPlaying: boolean
        videoPaused: boolean
        showTitleOverlay: boolean
        canAutoplay: boolean
        currentVideo: VideoDetails
        currentVideoMetadata: VideoMetaDTO | undefined
        currentVideoPlaylistType: PlaylistType
        currentState: Other | AutoplayCountdown
    }
    videoElement: React.ReactElement<any> | null
    /** Should be spread on the div wrapping the video element */
    wrapperElProps: {
        onMouseEnter: React.MouseEventHandler<HTMLElement>
        onMouseMove: React.MouseEventHandler<HTMLElement>
        onMouseLeave: React.MouseEventHandler<HTMLElement>
        innerRef: (el: HTMLElement | null) => void
    }
    publicUrl?: string
    currentPath?: string
    isLoading?: boolean
    adElement?: JSX.Element
    withH1?: boolean
    canAutoPlay?: boolean
    isVideoSticky?: boolean
}

export interface PNPlayerInterfaceProps extends ThemeProps, ViewportProps {
    videoDetails: VideoDetails
    videoOptions: VideoOptions
    pauseOverlay?: boolean
    /** false to disable autoplay */
    autoplayOptions: AutoplayOptions | false
    autoplayNextOptions: AutoplayOptions | false
    onEvent: (event: PlayerEvent) => void
    getVideoQueue: GetVideoQueue
    pagePosition: PagePosition
    /**
     * The `playWhenReady` prop tells the video to try and play the video. You can use this prop to try autoplay the
     * video immediately, but its preferable to use the autoplay options for that instead. This prop should only be used
     * if you need to effect the playing of the video component from its the parent component.
     */
    playWhenReady?: boolean
    onUserInteraction: (e: PlayerEvent) => void
    logger: Logger
    config: VideoConfig & RuntimeVideoConfig
    renderPlayer: (
        renderPlayerParams: PNRenderPlayerParams,
    ) => React.ReactElement<any>
    playerProvider: () => PlayerProvider
    showPlaylist?: boolean
    pathname: string
    isVideoHub?: boolean
    isVideoHubPlayListFixEnabled?: boolean
    onPlayNextVideo?: (nextVideo: VideoQueueItemMeta) => void
    // autoplay even on mobile devices
    canAutoPlay?: boolean
    isPerthNow?: boolean
    forcedMuted?: boolean
    onMuteChange?: (muted: boolean) => void
}

interface State {
    adStarted: boolean
    adPlayed: boolean
    adPlaying: boolean
    adPaused: boolean
    videoStarted: boolean
    videoPlaying: boolean
    videoPaused: boolean
    autoplayHasCompleted: boolean
    isFullscreen: boolean
    currentVideo: VideoDetails
    currentVideoMetadata: VideoMetaDTO | undefined
    initialVideoMetadata: VideoMetaDTO | undefined
    currentVideoPlaylistType: PlaylistType
    showTopBar: boolean
    playWhenReady: boolean
    videoPlayable: boolean
    showInternalPlayButton: boolean
    isMuted: boolean
    videoQueue: VideoQueue
    queuePosition: number
    canAutoplay: boolean

    // Will just trial using playerState for counting down
    playerState: Other | AutoplayCountdown
}

export class PNPlayerInterface extends React.Component<
    PNPlayerInterfaceProps,
    State
> {
    mounted = false
    playerOffsetWidth = 0
    autoplayInterval?: number

    constructor(props: PNPlayerInterfaceProps) {
        super(props)

        this.state = {
            adStarted: false,
            adPlayed: false,
            adPlaying: false,
            adPaused: false,
            videoStarted: false,
            videoPlaying: false,
            videoPaused: false,
            autoplayHasCompleted: false,
            isFullscreen: false,
            currentVideo: this.props.videoDetails,
            currentVideoMetadata: undefined,
            initialVideoMetadata: undefined,
            currentVideoPlaylistType: 'not-playlist-video',
            showTopBar: true,
            playWhenReady: props.playWhenReady || false,
            videoPlayable: false,
            showInternalPlayButton: true,
            isMuted: true,
            canAutoplay: false,
            videoQueue: {
                type: 'not-playlist-video',
                items: [],
            },
            // video hub needs a one-based index as initial video is part of queue playlist
            queuePosition: props.isVideoHub ? 1 : 0,
            playerState: { type: 'other' },
        }
    }

    async componentDidMount() {
        try {
            this.mounted = true
            const metadata = await getMetadata(
                this.state.currentVideo.id,
                this.state.currentVideo.accountId,
                this.props.config,
                this.props.logger,
            )
            if (!this.mounted) {
                return
            }

            const autoplayMuteState = await videoCanAutoplay({
                forceEnableAutoplay:
                    this.props.isVideoHub || this.props.canAutoPlay,
            })

            if (!this.mounted) {
                return
            }
            const canAutoplay =
                autoplayMuteState !== AutoplayAllowState.CannotAutoplay
            const shouldAutoplay =
                this.props.autoplayOptions !== false && canAutoplay

            // For some reason just checking shouldAutoplay is not enough
            if (
                shouldAutoplay &&
                this.props.autoplayOptions !== false &&
                this.state.playWhenReady === false
            ) {
                this.initAutoplay(this.props.autoplayOptions)
            }

            const currentVideo = {
                ...this.state.currentVideo,
                source: parseSourceFromMetadata(
                    metadata.source,
                    metadata.sourceExtra,
                ),
            }

            this.setState({
                currentVideo,
                currentVideoMetadata: metadata,
                initialVideoMetadata: metadata,
                isMuted: true,
                canAutoplay,
                showTopBar: true,
                showInternalPlayButton:
                    (!shouldAutoplay && !this.state.playWhenReady) || false,
            })
        } catch (err) {
            this.props.logger.error(
                { err: err as Error },
                'metadata get failed',
            )
        }
    }

    isPlaylistDifferent(
        prev: VideoQueueItemMeta[],
        curr: VideoQueueItemMeta[],
    ) {
        if (
            JSON.stringify([...curr].sort((a: any, b: any) => a.id - b.id)) !==
            JSON.stringify([...prev].sort((a: any, b: any) => a.id - b.id))
        ) {
            return true
        } else {
            return false
        }
    }

    async componentDidUpdate(prevProps: PNPlayerInterfaceProps) {
        if (prevProps.videoDetails?.id !== this.props.videoDetails.id) {
            const metadata = await getMetadata(
                this.props.videoDetails.id,
                this.props.videoDetails.accountId,
                this.props.config,
                this.props.logger,
            )

            const currentVideo = {
                ...this.props.videoDetails,
                source: parseSourceFromMetadata(
                    metadata.source,
                    metadata.sourceExtra,
                ),
            }

            this.setState({
                currentVideo,
                currentVideoMetadata: metadata,
                initialVideoMetadata: metadata,
            })
        }

        if (this.props.isVideoHubPlayListFixEnabled && this.props.isVideoHub) {
            const videoQueue = await this.props.getVideoQueue()
            if (
                this.isPlaylistDifferent(
                    this.state.videoQueue.items,
                    videoQueue.items,
                )
            ) {
                this.setState({
                    videoQueue,
                })
            }
        }

        if (
            !prevProps.playWhenReady &&
            this.props.playWhenReady &&
            !this.state.playWhenReady
        ) {
            this.playVideo()
        }

        if (
            this.props.autoplayOptions &&
            this.props.autoplayOptions.pauseWhenOutOfView
        ) {
            if (
                (prevProps.inViewport >= 50 && this.props.inViewport < 50) ||
                (document.hidden && !this.state.videoPaused)
            ) {
                this.pauseVideo()
            }

            if (prevProps.inViewport < 50 && this.props.inViewport >= 50) {
                this.resumeVideo()
            }
        }
    }

    componentWillUnmount() {
        this.mounted = false
        if (this.autoplayInterval !== undefined) {
            countdownTimer.clearInterval(this.autoplayInterval)
        }
    }

    initAutoplay = (autoplayOptions: AutoplayOptions) => {
        if (!this.mounted) {
            return
        }
        this.setState(
            {
                playerState: {
                    type: 'autoplay-countdown',
                    autoplayOptions,
                    remainingTime:
                        autoplayOptions && autoplayOptions.delayDuration
                            ? autoplayOptions.delayDuration
                            : 0,
                },
            },
            () => {
                this.autoplayInterval = countdownTimer.setInterval(
                    this.updateAutoplayTime,
                    1000,
                )
            },
        )
    }

    updateAutoplayTime = () => {
        if (this.state.playerState.type !== 'autoplay-countdown') {
            this.props.logger.error(
                {
                    playerState: this.state.playerState,
                },
                'Received updateAutoplayTime event when not in autoplay-countdown state',
            )
            return
        }

        // magic number "50" represents "50%" of the viewport height
        const isInViewport = this.props.inViewport > 50

        if (
            this.state.playerState.autoplayOptions
                .pauseCountdownOutsideViewport &&
            (isInViewport === false || document.hidden)
        ) {
            return
        }

        if (this.state.playerState.remainingTime > 0) {
            const remainingTime = this.state.playerState.remainingTime - 1
            return this.setState((state) => ({
                playerState: {
                    ...state.playerState,
                    remainingTime,
                },
            }))
        }

        this.autoplayComplete()
    }

    autoplayComplete = () => {
        if (this.autoplayInterval !== undefined) {
            countdownTimer.clearInterval(this.autoplayInterval)
        }
        this.setState({
            playerState: { type: 'other' },
            showTopBar: true,
            playWhenReady: true,
            autoplayHasCompleted: true,
            showInternalPlayButton: false,
        })
    }

    /** Cancels autoplay timer, call when user initiates video play when counting down */
    cancelAutoplay = () => {
        if (this.autoplayInterval !== undefined) {
            countdownTimer.clearInterval(this.autoplayInterval)
        }
        this.setState({
            playerState: { type: 'other' },
        })
    }

    /** User initiated stop of autoplay countdown */
    stopAutoplay = () => {
        this.cancelAutoplay()
        this.setState({
            autoplayHasCompleted: false,
            showInternalPlayButton: true,
        })
    }

    playerReady = async () => {
        if (!this.props.autoplayNextOptions) {
            return
        }

        try {
            // state.videoPlayable is not true until this is done
            const videoQueue = await this.props.getVideoQueue()
            this.setState({
                videoQueue,
            })
        } catch (err) {
            this.props.logger.error(
                { err: err as Error },
                `getVideoQueue() failed!`,
            )
        }
    }

    videoCompleted = async () => {
        const nextVideo = this.state.videoQueue.items[this.state.queuePosition]

        if (!nextVideo) {
            return
        }

        swmPlayerDebug(`Video play completed`)

        if (this.props.autoplayNextOptions !== false) {
            this.initAutoplay(this.props.autoplayNextOptions)
        }
    }

    variant = (): Standard['videoPlayerVariant'] => {
        return this.props.videoOptions.canAutoplayVideos
            ? 'Autoplay'
            : 'Default-BigPlayButton'
    }

    handleMuteToggle = () => {
        this.setState({ isMuted: !this.state.isMuted })
    }

    onPlayerEvent = async (event: PlayerEvent) => {
        swmPlayerDebug('Video event raised', event)

        switch (event.type) {
            case DataLayerEventName.playToPauseError:
                // ad is skipped in this condition (fails)
                // main video logs that it is playing in the background, but is not displayed
                this.setState({
                    showInternalPlayButton: this.props.isVideoHub
                        ? false
                        : true,
                    playWhenReady: false,
                    adPlaying: false,
                    adPlayed: true,
                })
                break
            case DataLayerEventName.playbackError:
                this.setState({
                    videoPlayable: true,
                })
                break
            case DataLayerEventName.playerReady:
                await this.playerReady()
                this.setState({
                    videoPlayable: true,
                })
                break
            case DataLayerEventName.videoAvailable:
                if (this.state.playWhenReady) {
                    this.playVideo()
                }
                break
            case DataLayerEventName.videoPlay:
                this.cancelAutoplay()
                this.setState({
                    showInternalPlayButton: false,
                    playWhenReady: false,
                    videoPlaying: true,
                })
                this.props.onUserInteraction(event)
                return
            case DataLayerEventName.videoStart:
                this.setState({
                    showInternalPlayButton: false,
                    videoStarted: true,
                    adPlayed: true,
                    adPlaying: false,
                    adPaused: false,
                    videoPlaying: true,
                    videoPaused: false,
                })
                break
            case DataLayerEventName.videoPause:
                this.setState({
                    showInternalPlayButton: false,
                    adPlayed: true,
                    videoPlaying: false,
                    showTopBar: true,
                })
                break
            case DataLayerEventName.videoEnd:
                this.videoCompleted()
                break
            case DataLayerEventName.adError:
                break
            case DataLayerEventName.adStart:
                this.cancelAutoplay()
                this.setState({
                    showInternalPlayButton: false,
                    adStarted: true,
                    adPlayed: false,
                    adPlaying: true,
                    adPaused: false,
                    showTopBar: true,
                })
                break
            case DataLayerEventName.adPause:
                this.setState({
                    adPlayed: false,
                    adPlaying: false,
                    adPaused: true,
                })
                break
            case DataLayerEventName.adPlay:
                this.setState({
                    adPlayed: false,
                    adPlaying: true,
                    adPaused: false,
                })
                break
            case DataLayerEventName.adEnd:
                this.setState({
                    adPlayed: true,
                    adPaused: false,
                    adPlaying: false,
                })
                break
            case DataLayerEventName.adPodComplete:
                this.setState({
                    adPlayed: true,
                    adPlaying: false,
                    adPaused: false,
                })
                break
            case DataLayerEventName.playerFullscreenEnter:
                this.setState({ isFullscreen: true })
                break
            case DataLayerEventName.playerFullscreenExit:
                this.setState({ isFullscreen: false })
                break
            case DataLayerEventName.playerUserInactive:
                // Show controls always when paused and mouse on video player element
                // Will dismiss on mouseLeave or delayed after playing
                !this.state.videoPaused && this.setState({ showTopBar: false })
                break
            case DataLayerEventName.playerUserActive:
                this.setState({ showTopBar: true })
                break
            case DataLayerEventName.videoVolumeChange:
                if (!event.payload.muted) {
                    this.setState({ isMuted: false })
                }
                break
            case DataLayerEventName.adVolumeChange:
                if (!event.payload.muted) {
                    this.setState({ isMuted: false })
                }
                break
        }

        this.props.onEvent(event)
    }

    handleMouseEnter = (_event: React.MouseEvent<HTMLDivElement>) => {
        this.setState({ showTopBar: true })
    }

    handleMouseMove = (_event: React.MouseEvent<HTMLDivElement>) => {
        if (!this.state.showTopBar) {
            this.setState({ showTopBar: true })
        }
    }

    handleMouseLeave = (_event: React.MouseEvent<HTMLDivElement>) => {
        // Issue with adPlayer where hovering from video to the ad control bar
        // counts as a mouse leave and flickers in and out of view
        if (this.state.adPlayed || this.state.videoStarted) {
            this.setState({ showTopBar: false })
        }
    }

    handlePlaylistScroll = () => {
        this.props.onUserInteraction({
            type: DataLayerEventName.playlistScroll,
            originator: 'playlist-event',
            payload: {},
        })
    }

    setRef = (playerVideo: HTMLElement | null) => {
        if (playerVideo) {
            this.playerOffsetWidth = playerVideo.offsetWidth
        }
        this.props.innerRef(playerVideo)
    }

    playVideo = () => {
        // The video provider component waits for a prop change to trigger a play,
        // this callback ensures the prop will change from false->true preventing edge case state issues
        this.setState(
            {
                playWhenReady: false,
            },
            () => {
                this.setState({
                    playWhenReady: true,
                    showInternalPlayButton: false,
                    videoPaused: false,
                    videoPlaying: this.state.adPlayed ? true : false,
                    adPlaying: this.state.adPlayed ? true : false,
                    adPaused: false,
                })
            },
        )
    }

    pauseVideo = () => {
        this.setState({
            videoPaused: this.state.adPlayed ? true : false,
            videoPlaying: false,
            adPlaying: false,
            adPaused: this.state.adPlayed ? true : false,
        })
    }

    resumeVideo = () => {
        this.setState(() => {
            this.setState({
                videoPaused: false,
                videoPlaying: true,
                playWhenReady: true,
            })
        })
    }

    handleUserInactive = () => {
        this.setState({
            showTopBar: false,
        })
    }

    render() {
        const { videoOptions } = this.props
        const PlayerProviderComponent = this.props.playerProvider()
        const playerState = {
            isMuted: this.state.isMuted,
            adStarted: this.state.adStarted,
            adPlayed: this.state.adPlayed,
            adPlaying: this.state.adPlaying,
            adPaused: this.state.adPaused,
            videoStarted: this.state.videoStarted,
            videoPlaying: this.state.videoPlaying,
            videoPaused: this.state.videoPaused,
            canAutoplay: this.state.canAutoplay,
            currentVideo: this.state.currentVideo,
            currentVideoMetadata: this.state.currentVideoMetadata,
            currentVideoPlaylistType: this.state.currentVideoPlaylistType,
            isFullscreen: this.state.isFullscreen,
            showInternalPlayButton: this.state.showInternalPlayButton,
            showTitleOverlay:
                this.state.playerState.type === 'autoplay-countdown',
            showTopBar: this.state.showTopBar,
            inViewport: this.props.inViewport,
            currentState: this.state.playerState,
        }

        return this.props.renderPlayer({
            theme: this.props.theme,
            playerActions: {
                handleMuteToggle: this.handleMuteToggle,
                playVideo: this.playVideo,
                stopAutoplay: this.stopAutoplay,
                pauseVideo: this.pauseVideo,
                resumeVideo: this.resumeVideo,
                handleUserInactive: this.handleUserInactive,
            },
            playerOptions: videoOptions,
            playerState,
            wrapperElProps: {
                innerRef: this.setRef,
                onMouseEnter: this.handleMouseEnter,
                onMouseLeave: this.handleMouseLeave,
                onMouseMove: this.handleMouseMove,
            },
            // Need the surrounding div to ensure the videoJS dom manipulations
            // do not cause errors
            videoElement: this.state.currentVideoMetadata ? (
                <div>
                    <PlayerProviderComponent
                        logger={this.props.logger}
                        config={this.props.config}
                        playerOptions={{
                            ...videoOptions,
                            brightcovePlayerId: brightcovePlayerWebId,
                        }}
                        isMuted={this.state.isMuted}
                        adPlaying={this.state.adPlaying}
                        autoplayOptions={this.props.autoplayOptions}
                        autoplayHasCompleted={this.state.autoplayHasCompleted}
                        play={
                            this.state.videoPlayable && this.state.playWhenReady
                        }
                        pause={this.state.videoPaused}
                        currentVideo={this.state.currentVideo}
                        currentVideoMetadata={this.state.currentVideoMetadata}
                        currentVideoPosition={this.state.queuePosition}
                        playlistType={this.state.currentVideoPlaylistType}
                        isFullscreen={this.state.isFullscreen}
                        onEvent={this.onPlayerEvent}
                        pagePosition={this.props.pagePosition}
                        variant={this.variant()}
                        product={this.props.theme.kind}
                        pathname={this.props.pathname}
                        isVideoHub={this.props.isVideoHub}
                    />
                </div>
            ) : null,
        })
    }
}
